import numeral from 'numeral'
import pluralize from 'pluralize'
import { useMemo } from 'react'

import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { Paragraph, SpanDisplayAlt } from 'src/components/atoms/Typography'
import {
  calculateCarbon,
  withDefaults
} from 'src/components/organisms/CarbonCalculator/helper'
import { CalculatorTab } from 'src/components/organisms/CarbonCalculator/types'

import { carbonCalculatorPath } from 'src/config/paths'
import { FunderStatsRouterQuery } from 'src/server/router/accounts/type'
import { listCertifications } from 'src/utils/certifications'

import { FunderCertifications } from '../FunderCertifications'
import { AllTimeStat } from './AllTimeStats'
import { SeasonalStats } from './SeasonalStats'
import { orderSeasonalDataArray } from './helper'

export interface ProfileStatsCardProps {
  funderData?: FunderStatsRouterQuery
}

export const ProfileStatsCard = ({ funderData }: ProfileStatsCardProps) => {
  const funderStats = funderData?.funderStats || null

  const allSeasonFunding = funderStats?.find(
    (element: { SeasonName: string }) => element.SeasonName === 'All'
  )

  const certificationData = useMemo(() => {
    return funderStats ? listCertifications(funderStats) : []
  }, [funderStats])

  const orderedSeasonalFundingData = orderSeasonalDataArray(funderStats)

  const totalTrees = allSeasonFunding?.TreesFunded || 0

  const carbon = useMemo(() => {
    if (totalTrees === 0 || totalTrees === undefined || totalTrees === null) {
      return 0
    }

    const res = calculateCarbon(
      withDefaults({
        formTab: CalculatorTab.Stems,
        stems: totalTrees
      })
    )

    return Math.ceil(res.co2Total)
  }, [totalTrees])

  const carbonFormatted = numeral(carbon).format('0,0')

  return (
    <>
      <Paragraph className='text-tomThumb font-display-rebrand text-4xl! uppercase'>
        funding stats
      </Paragraph>
      {allSeasonFunding && allSeasonFunding.TreesFunded > 0 && (
        <>
          {/* <Show when={allSeasonFunding?.Rank}>
            <RankStat rank={allSeasonFunding?.Rank!} />
          </Show> */}
          <div className='lg:flex text-center mt-4'>
            <AllTimeStat
              statistic={numeral(allSeasonFunding?.TreesFunded).format('0,0')}
              subtitleText='total trees funded'
            />
            <AllTimeStat
              statistic={numeral(allSeasonFunding?.ProjectSupported).format(
                '0,0'
              )}
              subtitleText={`${pluralize(
                'project',
                allSeasonFunding?.ProjectSupported
              )} Supported`}
            />
          </div>
          <SeasonalStats
            seasonalStats={orderedSeasonalFundingData}
            valueHeader='Trees Funded'
          />
        </>
      )}
      {!allSeasonFunding || allSeasonFunding.TreesFunded === 0 ? (
        <Paragraph>This user has yet to fund any trees.</Paragraph>
      ) : (
        <div className='lg:flex lg:items-center lg:space-x-4 mt-6 pb-4'>
          <Image
            className='lg:shrink-0 mb-2'
            alt='Co2 icon'
            src='/img/icons/rebranded/carbon-forest-green.svg'
            width={60}
            height={60}
          />
          <Paragraph className='lg:w-5/6'>
            <SpanDisplayAlt className='text-tomThumb'>
              {numeral(allSeasonFunding?.TreesFunded).format('0,0')}{' '}
              {pluralize('trees', allSeasonFunding?.TreesFunded)}
            </SpanDisplayAlt>{' '}
            will help to remove about{' '}
            <SpanDisplayAlt className='text-tomThumb'>
              {carbonFormatted} {pluralize('tonnes', parseInt(carbonFormatted))}
            </SpanDisplayAlt>{' '}
            of carbon dioxide from our atmosphere over 50 years.{' '}
            <InternalLink href={carbonCalculatorPath}>Learn more</InternalLink>
          </Paragraph>
        </div>
      )}
      {certificationData && (
        <FunderCertifications certificationData={certificationData} />
      )}
    </>
  )
}
